import { Store } from "vuex";
import { Container } from "inversify";
import { Api } from "../../common/src/api/Api";
import { ApiFactory } from "../../common/src/api/ApiFactory";
import { AuthStore } from "../../common/src/stores/AuthStore";
import axios from "axios";
import { OrganizationListStore } from "./stores/organization/OrganizationListStore";
import { UserFavoriteOrganizationListStore } from "./stores/user/UserFavoriteOrganizationListStore";
import { OrganizationCollectionStore as ExplorerToolOrganizationCollectionStore } from "./stores/explorer-tool/OrganizationCollectionStore";
import { OrganizationCollectionStore as IndustryAnalyticsOrganizationCollectionStore } from "./stores/industry-analytics/OrganizationCollectionStore";
import { OrganizationCollectionStore as GeographicOrganizationCollectionStore } from "./stores/geographic-tool/OrganizationCollectionStore";
import { OrganizationCollectionStore as UserFavoriteOrganizationCollectionStore } from "./stores/user/OrganizationCollectionStore";
import { OrganizationStore } from "./stores/organization/OrganizationStore";
import { OrganizationFilterStore } from "./stores/organization/OrganizationFilterStore";
import { ReportFilterStore } from "./stores/reports/ReportFilterStore";
import { ReportCollectionStore as SectorCompanyReportsCollectionStore } from "./stores/reports/ReportCollectionStore";
import { FlashNewsFilterStore } from "./stores/flashNews/FlashNewsFilterStore";
import { FlashNewsCollectionStore } from "./stores/flashNews/FlashNewsCollectionStore";
import { QuiltyInsightsFilterStore } from "./stores/quiltyInsights/QuiltyInsightsFilterStore";
import { QuiltyInsightsCollectionStore } from "./stores/quiltyInsights/QuiltyInsightsCollectionStore";
import { Axios } from "@gsx/common";
import { ConferenceListStore } from "./stores/conference/ConferenceListStore";
import { ConferenceCreateStore } from "./stores/conference/ConferenceCreateStore";
import { ConferenceEditStore } from "./stores/conference/ConferenceEditStore";
import { ConferenceDestroyStore } from "./stores/conference/ConferenceDestroyStore";
import { ConferenceCollectionStore } from "./stores/conference/ConferenceCollectionStore";
import { ConferenceFilterStore } from "./stores/conference/ConferenceFilterStore";
import { RecaptchaService } from "../../common/src/utility/recaptcha/RecaptchaService";
import { registerModules } from "../../common/src/utility/containerUtils";
import { StatisticsStore } from "./stores/organization/StatisticsStore";
import { UserEditFavoriteStore } from "./stores/user/UserEditFavoriteStore";
import { AnalyticFavoriteManager } from "./utils/AnalyticFavoriteManager";
import {
    UserActivityLogsListStore as DashboardUserActivityLogsListStore,
    UserActivityLogsListStore,
} from "../../dashboard/src/stores/user/UserActivityLogsListStore";
import { UserVisitedOrganizationListStore as DashboardUserVisitedOrganizationListStore } from "../../dashboard/src/stores/user/UserVisitedOrganizationListStore";
import { UserActivityLogsListStore as AdminUserActivityLogsListStore } from "../../landing/src/stores/admin/user/UserActivityLogsListStore";
import { UserFavoriteConferenceListStore } from "./stores/user/UserFavoriteConferenceListStore";
import { RequestListStore as MemberRequestListStore } from "../../landing/src/stores/member/requests/RequestListStore";
import { ReportListStore as DashboardReportListStore } from "./stores/reports/ReportListStore";
import { FlashNewsListStore as DashboardFlashNewsListStore } from "./stores/flashNews/FlashNewsListStore";
import { QuiltyInsightsListStore as DashboardQuiltyInsightsListStore } from "./stores/quiltyInsights/QuiltyInsightsListStore";
import { PaymentService } from "../../landing/src/utility/payment/PaymentService";
import { FirstTimeDashboardVisitSidebarStore } from "./molecules/user-dashboard/FirstTimeDashboardVisitSidebarStore";
import { AnalyticFiltering } from "./molecules/industry-analytics/AnalyticFiltering";
import { OrganizationSimilarOrganizationStore } from "./stores/organization/resource/OrganizationSimilarOrganizationStore";
import { MergerAcquisitionsListStore as DashbaordMergerAcquisitionsListStore } from "./stores/mergerAcquisitions/MergerAcquisitionsListStore";
import { OrganizationMergerAcquisitionsListStore as DashbaordOrganizationMergerAcquisitionsListStore } from "./stores/mergerAcquisitions/OrganizationMergerAcquisitionsListStore";
import { MergerAcquisitionsCollectionStore as DashbaordMergerAcquisitionsCollectionStore } from "./stores/mergerAcquisitions/MergerAcquisitionsCollectionStore";
import { OrganizationProjectsCollectionStore as DashboardOrganizationProjectsCollectionStore } from "./stores/organization/resource/OrganizationProjectsCollectionStore";

import { OrderListStore } from "../../landing/src/stores/order/OrderListStore";
import { QuiltyResourcesListStore } from "./stores/quiltyResources/QuiltyResourcesListStore";
import axiosRetry from "axios-retry";

export const createContainer = (store: Store<any>): Container => {
    const container = new Container({
        autoBindInjectable: true,
        skipBaseClassChecks: true,
    });
    container.bind(Container).toConstantValue(container);

    container
        .bind(Axios)
        .toDynamicValue(() => {
            const instance = axios.create({
                withCredentials: true,
            });

            instance.interceptors.request.use((config) => {
                if (config.url?.startsWith(process.env.API_URL!)) {
                    const authStore: AuthStore = container.get(AuthStore);

                    if (authStore.token) {
                        config.headers = {
                            Authorization: `JWT ${authStore.token}`,
                        };
                    }
                }

                return config;
            });

            axiosRetry(instance, {
                retries: 3,
                retryCondition: (error) =>
                    axiosRetry.isNetworkOrIdempotentRequestError(error) ||
                    error.response?.status === 500,
                retryDelay: axiosRetry.exponentialDelay,
            });

            return instance;
        })
        .inSingletonScope();

    container.bind(ApiFactory).toDynamicValue(() => {
        const axiosInstance = container.get<Axios>(Axios);
        return new ApiFactory(axiosInstance, process.env.API_URL!);
    });

    registerModules(container, store);

    container
        .bind(DashboardReportListStore)
        .toDynamicValue(
            () =>
                new DashboardReportListStore(container.get(Api), container.get(ReportFilterStore), {
                    store,
                    name: "dashboardReportListStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(DashboardFlashNewsListStore)
        .toDynamicValue(
            () =>
                new DashboardFlashNewsListStore(
                    container.get(Api),
                    container.get(FlashNewsFilterStore),
                    {
                        store,
                        name: "flashNewsListStore",
                    },
                ),
        )
        .inSingletonScope();

    container
        .bind(DashboardQuiltyInsightsListStore)
        .toDynamicValue(
            () =>
                new DashboardQuiltyInsightsListStore(container.get(Api), {
                    store,
                    name: "quiltyInsightsListStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(ConferenceListStore)
        .toDynamicValue(
            () =>
                new ConferenceListStore(container.get(Api), container.get(ConferenceFilterStore), {
                    store,
                    name: "conferenceList",
                }),
        )
        .inSingletonScope();

    container
        .bind("ConferenceTableListStore")
        .toDynamicValue(
            () =>
                new ConferenceListStore(container.get(Api), container.get(ConferenceFilterStore), {
                    store,
                    name: "conferenceTableList",
                }),
        )
        .inSingletonScope();

    container
        .bind("ConferenceMapListStore")
        .toDynamicValue(
            () =>
                new ConferenceListStore(container.get(Api), container.get(ConferenceFilterStore), {
                    store,
                    name: "conferenceMapList",
                }),
        )
        .inSingletonScope();

    container
        .bind("ConferenceCategoryListStore")
        .toDynamicValue(
            () =>
                new ConferenceListStore(container.get(Api), container.get(ConferenceFilterStore), {
                    store,
                    name: "conferenceCategoryList",
                }),
        )
        .inSingletonScope();

    container
        .bind(OrderListStore)
        .toDynamicValue(
            () =>
                new OrderListStore(container.get(Api), {
                    store,
                    name: "orderListStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(UserFavoriteConferenceListStore)
        .toDynamicValue(
            () =>
                new UserFavoriteConferenceListStore(container.get(Api), {
                    store,
                    name: "userFavoriteConferenceListStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(DashboardOrganizationProjectsCollectionStore)
        .toDynamicValue(
            () =>
                new DashboardOrganizationProjectsCollectionStore(container.get(Api), {
                    store,
                    name: "dashboardOrganizationProjectsCollectionStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(OrganizationListStore)
        .toDynamicValue(
            () =>
                new OrganizationListStore(
                    container.get(Api),
                    container.get(OrganizationFilterStore),
                    {
                        store,
                        name: "organizationList",
                    },
                ),
        )
        .inSingletonScope();

    container
        .bind("OrganizationMapCoordinatesListStore")
        .toDynamicValue(
            () =>
                new OrganizationListStore(
                    container.get(Api),
                    container.get(OrganizationFilterStore),
                    {
                        store,
                        name: "organizationMapCoordinatesListStore",
                    },
                ),
        )
        .inSingletonScope();

    container
        .bind("OrganizationSearchListStore")
        .toDynamicValue(
            () =>
                new OrganizationListStore(
                    container.get(Api),
                    container.get("OrganizationSearchFilterStore"),
                    {
                        store,
                        name: "organizationSearchList",
                    },
                ),
        )
        .inSingletonScope();

    container
        .bind("OrganizationExplorerListStore")
        .toDynamicValue(
            () =>
                new OrganizationListStore(
                    container.get(Api),
                    container.get(OrganizationFilterStore),
                    {
                        store,
                        name: "organizationExplorerList",
                    },
                ),
        )
        .inSingletonScope();

    container
        .bind(QuiltyResourcesListStore)
        .toDynamicValue(
            () =>
                new QuiltyResourcesListStore(container.get(Api), {
                    store,
                    name: "quiltyResourceList",
                }),
        )
        .inSingletonScope();

    // container
    //     .bind(SubscriptionService)
    //     .toDynamicValue(
    //         () =>
    //             new SubscriptionService(
    //                 container.get(Axios),
    //                 container.get(UrlFactory),
    //             )
    //     )

    container
        .bind(UserFavoriteOrganizationListStore)
        .toDynamicValue(
            () =>
                new UserFavoriteOrganizationListStore(container.get(Api), {
                    store,
                    name: "userFavoriteOrganizationListStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(DashbaordMergerAcquisitionsListStore)
        .toDynamicValue(
            () =>
                new DashbaordMergerAcquisitionsListStore(container.get(Api), {
                    store,
                    name: "dashbaordMergerAcquisitionsListStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(DashbaordOrganizationMergerAcquisitionsListStore)
        .toDynamicValue(
            () =>
                new DashbaordOrganizationMergerAcquisitionsListStore(container.get(Api), {
                    store,
                    name: "dashbaordOrganizationMergerAcquisitionsListStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(SectorCompanyReportsCollectionStore)
        .toDynamicValue(
            () =>
                new SectorCompanyReportsCollectionStore(container.get(DashboardReportListStore), {
                    store,
                    name: "sectorCompanyReportsCollectionStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(FlashNewsCollectionStore)
        .toDynamicValue(
            () =>
                new FlashNewsCollectionStore(container.get(DashboardFlashNewsListStore), {
                    store,
                    name: "flashNewsCollectionStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(QuiltyInsightsCollectionStore)
        .toDynamicValue(
            () =>
                new QuiltyInsightsCollectionStore(container.get(DashboardQuiltyInsightsListStore), {
                    store,
                    name: "quiltyInsightsCollectionStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(DashbaordMergerAcquisitionsCollectionStore)
        .toDynamicValue(
            () =>
                new DashbaordMergerAcquisitionsCollectionStore(
                    container.get(DashbaordMergerAcquisitionsListStore),
                    {
                        store,
                        name: "dashbaordMergerAcquisitionsCollectionStore",
                    },
                ),
        )
        .inSingletonScope();

    container
        .bind(UserFavoriteOrganizationCollectionStore)
        .toDynamicValue(
            () =>
                new UserFavoriteOrganizationCollectionStore(
                    container.get(UserFavoriteOrganizationListStore),
                    {
                        store,
                        name: "userFavoriteOrganizationCollectionStore",
                    },
                ),
        )
        .inSingletonScope();

    container
        .bind(ExplorerToolOrganizationCollectionStore)
        .toDynamicValue(
            () =>
                new ExplorerToolOrganizationCollectionStore(container.get(OrganizationListStore), {
                    store,
                    name: "industryExplorerOrganizationCollection",
                }),
        )
        .inSingletonScope();

    container
        .bind(IndustryAnalyticsOrganizationCollectionStore)
        .toDynamicValue(
            () =>
                new IndustryAnalyticsOrganizationCollectionStore(
                    container.get(OrganizationListStore),
                    {
                        store,
                        name: "industryAnalyticsOrganizationCollection",
                    },
                ),
        )
        .inSingletonScope();

    container
        .bind(GeographicOrganizationCollectionStore)
        .toDynamicValue(
            () =>
                new GeographicOrganizationCollectionStore(container.get(OrganizationListStore), {
                    store,
                    name: "geographicOrganizationCollection",
                }),
        )
        .inSingletonScope();

    container
        .bind(ConferenceCollectionStore)
        .toDynamicValue(
            () =>
                new ConferenceCollectionStore(container.get("ConferenceTableListStore"), {
                    store,
                    name: "conferenceCollection",
                }),
        )
        .inSingletonScope();

    container
        .bind(UserEditFavoriteStore)
        .toDynamicValue(
            () =>
                new UserEditFavoriteStore(
                    container.get(Api),
                    container.get(UserFavoriteConferenceListStore),
                    container.get(UserFavoriteOrganizationListStore),
                    container.get(UserActivityLogsListStore),
                    container.get(OrganizationFilterStore),
                    {
                        store,
                        name: "userEditFavoriteStore",
                    },
                ),
        )
        .inSingletonScope();

    container
        .bind(AnalyticFavoriteManager)
        .toDynamicValue(
            () =>
                new AnalyticFavoriteManager(
                    container.get(AuthStore),
                    container.get(UserEditFavoriteStore),
                ),
        );

    container
        .bind(AnalyticFiltering)
        .toDynamicValue(() => new AnalyticFiltering(container.get(OrganizationListStore)));

    container
        .bind(DashboardUserActivityLogsListStore)
        .toDynamicValue(
            () =>
                new DashboardUserActivityLogsListStore(container.get(Api), {
                    store,
                    name: "userActivityLogsListStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(DashboardUserVisitedOrganizationListStore)
        .toDynamicValue(
            () =>
                new DashboardUserVisitedOrganizationListStore(container.get(Api), {
                    store,
                    name: "userVisitedOrganizationListStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(MemberRequestListStore)
        .toDynamicValue(
            () =>
                new MemberRequestListStore(container.get(Api), {
                    store,
                    name: "memberRequestList",
                }),
        )
        .inSingletonScope();

    container
        .bind(AdminUserActivityLogsListStore)
        .toDynamicValue(
            () =>
                new AdminUserActivityLogsListStore(container.get(Api), {
                    store,
                    name: "userActivityLogsListStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(OrganizationFilterStore)
        .toDynamicValue(
            () =>
                new OrganizationFilterStore({
                    store,
                    name: "organizationFilter",
                }),
        )
        .inSingletonScope();

    container
        .bind("OrganizationSearchFilterStore")
        .toDynamicValue(
            () =>
                new OrganizationFilterStore({
                    store,
                    name: "organizationSearchFilter",
                }),
        )
        .inSingletonScope();

    container
        .bind("OrganizationExplorerFilterStore")
        .toDynamicValue(
            () =>
                new OrganizationFilterStore({
                    store,
                    name: "organizationExplorerFilterStore",
                }),
        )
        .inSingletonScope();

    container
        .bind(ReportFilterStore)
        .toDynamicValue(
            () =>
                new ReportFilterStore({
                    store,
                    name: "reportFilter",
                }),
        )
        .inSingletonScope();

    container
        .bind(FlashNewsFilterStore)
        .toDynamicValue(
            () =>
                new FlashNewsFilterStore({
                    store,
                    name: "flashNewsFilter",
                }),
        )
        .inSingletonScope();

    container
        .bind(QuiltyInsightsFilterStore)
        .toDynamicValue(
            () =>
                new QuiltyInsightsFilterStore({
                    store,
                    name: "quiltyInsightsFilter",
                }),
        )
        .inSingletonScope();

    container
        .bind(ConferenceFilterStore)
        .toDynamicValue(() => new ConferenceFilterStore({ store, name: "conferenceFilter" }))
        .inSingletonScope();

    container
        .bind(OrganizationStore)
        .toDynamicValue(
            () => new OrganizationStore(container.get(Api), { store, name: "organizationItem" }),
        )
        .inSingletonScope();

    container
        .bind(OrganizationSimilarOrganizationStore)
        .toDynamicValue(
            () =>
                new OrganizationSimilarOrganizationStore(container.get(Api), {
                    store,
                    name: "similarOrganizations",
                }),
        )
        .inSingletonScope();

    container
        .bind(ConferenceDestroyStore)
        .toDynamicValue(
            () =>
                new ConferenceDestroyStore(container.get(Api), {
                    store,
                    name: "conferenceDestroy",
                }),
        )
        .inSingletonScope();

    container
        .bind(StatisticsStore)
        .toDynamicValue(
            () =>
                new StatisticsStore(container.get(Api), {
                    store,
                    name: "statistics",
                }),
        )
        .inSingletonScope();

    container.bind(ConferenceCreateStore).toSelf().inSingletonScope();

    container.bind(ConferenceEditStore).toSelf().inSingletonScope();

    container
        .bind(RecaptchaService)
        .toDynamicValue(() => new RecaptchaService(process.env.RECAPTCHA_SITE_KEY))
        .inSingletonScope();

    container
        .bind(PaymentService)
        .toDynamicValue(
            () => new PaymentService(container.get(Api), process.env.STRIPE_PUBLISHABLE),
        )
        .inSingletonScope();

    container.bind(FirstTimeDashboardVisitSidebarStore).toSelf().inSingletonScope();

    return container;
};
